import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FileUp, Loader2, X, CheckCircle2, AlertTriangle, Trash2, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";

const IMPORT_CATEGORIES = [
  "Cables y Conductores", "Tableros y Protecciones", "Luminarias e Iluminación",
  "Materiales de Soporte", "Transformadores y Equipos", "EPP", "Herramientas Menores", "Otros",
];
const IMPORT_UNITS = ["metro", "unidad", "rollo", "kg", "litro", "caja", "par", "juego"];

/**
 * BLOQUE 5 PARTE B — Importar materiales al Inventario desde Excel/PDF/imagen
 * de una cotización de proveedor, usando IA (Gemini). A diferencia del import
 * de Pedidos de Materiales, ACÁ SÍ se extraen precios y se crea/actualiza stock.
 */
export function ImportMaterialsModal({ open, onClose, onConfirmed, projects }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // rows
  const [fileName, setFileName] = useState("");
  const [locationId, setLocationId] = useState("almacen_central");
  const [confirming, setConfirming] = useState(false);
  const fileInputRef = useRef(null);

  const locationOptions = [
    { id: "almacen_central", name: "Almacén Central" },
    ...(projects || []).filter(p => p.status === "activo").map(p => ({ id: p._id || p.id, name: p.name })),
  ];
  const locationName = locationOptions.find(l => l.id === locationId)?.name || "Almacén Central";

  const reset = () => {
    setPreview(null);
    setFileName("");
    setUploading(false);
    setLocationId("almacen_central");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/inventory/materials/import`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = (res.data?.items || []).map(it => ({ ...it }));
      setPreview(items);
      toast.success(`${items.length} material${items.length !== 1 ? "es" : ""} detectado${items.length !== 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar el archivo");
      setFileName("");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateRow = (idx, field, value) => {
    setPreview(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const toggleRow = (idx) => {
    setPreview(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  const handleConfirm = async () => {
    const chosen = (preview || []).filter(r => r.selected && r.name.trim());
    if (!chosen.length) { toast.error("Seleccioná al menos un material para importar"); return; }
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/inventory/materials/import/confirm`, {
        items: chosen.map(({ category_needs_review, duplicate_material_stock, duplicate_material_name, price_includes_iva, ...rest }) => rest),
        location_id: locationId,
        location_name: locationName,
        file_name: fileName,
      }, { withCredentials: true });
      toast.success(res.data?.message || "Materiales importados");
      reset();
      onConfirmed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar la importación");
    } finally {
      setConfirming(false);
    }
  };

  const selectedCount = (preview || []).filter(r => r.selected).length;

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="import-materials-overlay" onClick={() => !uploading && !confirming && handleClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-materials-modal" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><Sparkles size={17} className="text-violet-600" /></div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Importar Materiales desde Excel/PDF</h2>
              <p className="text-xs text-gray-400 mt-0.5">Extracción automática con IA — precio, cantidad, nombre, unidad y categoría</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="import-materials-close"><X size={18} /></button>
        </div>

        <div className="p-6">
          {!preview ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Subí una cotización o presupuesto de proveedor (Excel .xlsx/.xls, PDF o imagen) y la IA
                extraerá automáticamente materiales, cantidades, precios unitarios, unidades y categorías sugeridas.
              </p>
              <label
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-10 cursor-pointer hover:border-violet-400 hover:bg-violet-50/30 transition-colors"
                data-testid="import-materials-dropzone"
              >
                {uploading ? (
                  <>
                    <Loader2 size={28} className="text-violet-600 animate-spin" />
                    <p className="text-sm text-gray-600 font-medium">Analizando "{fileName}" con IA...</p>
                    <p className="text-[11px] text-gray-400">Esto puede tardar unos segundos, sobre todo con PDFs largos.</p>
                  </>
                ) : (
                  <>
                    <FileUp size={28} className="text-gray-400" />
                    <p className="text-sm text-gray-600 font-medium">Hacé clic para elegir un archivo</p>
                    <p className="text-[11px] text-gray-400">.xlsx, .xls, .pdf, .png, .jpg — máx. 15MB</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploading}
                  data-testid="import-materials-file-input"
                />
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <p className="text-xs font-semibold text-emerald-800">
                    {preview.length} material{preview.length !== 1 ? "es" : ""} detectado{preview.length !== 1 ? "s" : ""} en "{fileName}" — revisá y editá antes de confirmar
                  </p>
                </div>
                <button type="button" onClick={reset} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1" data-testid="import-materials-reset-btn">
                  <Trash2 size={12} /> Cargar otro archivo
                </button>
              </div>

              <div className="flex items-center gap-3 bg-[#EBF2F9] rounded-xl px-4 py-2.5">
                <label className="text-xs font-semibold text-[#2E6DA4] whitespace-nowrap">Ubicación destino del stock inicial:</label>
                <select value={locationId} onChange={e => setLocationId(e.target.value)} className="border border-[#2E6DA4]/30 rounded-lg px-2 py-1.5 text-xs bg-white flex-1 max-w-xs" data-testid="import-materials-location-select">
                  {locationOptions.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              {preview.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <AlertTriangle size={28} className="mx-auto mb-2" />
                  <p className="text-sm">No se detectaron materiales válidos.</p>
                </div>
              ) : (
                <div className="max-h-[45vh] overflow-y-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-gray-500 uppercase text-[10px]">
                        <th className="py-2 px-2 text-left w-8"></th>
                        <th className="py-2 px-2 text-left">Nombre</th>
                        <th className="py-2 px-2 text-left w-28">Marca</th>
                        <th className="py-2 px-2 text-left w-20">Cant.</th>
                        <th className="py-2 px-2 text-left w-24">UM</th>
                        <th className="py-2 px-2 text-left w-28">Costo Unit. ₲</th>
                        <th className="py-2 px-2 text-left w-40">Categoría</th>
                        <th className="py-2 px-2 text-left w-44">Duplicado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.map((row, i) => (
                        <tr key={i} className={row.selected ? "" : "opacity-40"} data-testid={`import-materials-row-${i}`}>
                          <td className="py-1.5 px-2">
                            <input type="checkbox" checked={row.selected} onChange={() => toggleRow(i)} data-testid={`import-materials-row-check-${i}`} />
                          </td>
                          <td className="py-1.5 px-2">
                            <input value={row.name} onChange={e => updateRow(i, "name", e.target.value)}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`import-materials-row-name-${i}`} />
                          </td>
                          <td className="py-1.5 px-2">
                            <input value={row.marca || ""} onChange={e => updateRow(i, "marca", e.target.value)}
                              placeholder="—"
                              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`import-materials-row-marca-${i}`} />
                          </td>
                          <td className="py-1.5 px-2">
                            <input type="number" min="0" step="any" value={row.quantity}
                              onChange={e => updateRow(i, "quantity", Number(e.target.value))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-center" data-testid={`import-materials-row-qty-${i}`} />
                          </td>
                          <td className="py-1.5 px-2">
                            <select value={row.unit} onChange={e => updateRow(i, "unit", e.target.value)}
                              className="w-full border border-gray-200 rounded-lg px-1 py-1 text-xs bg-white">
                              {!IMPORT_UNITS.includes(row.unit) && <option value={row.unit}>{row.unit}</option>}
                              {IMPORT_UNITS.map(u => <option key={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="py-1.5 px-2">
                            <input type="number" min="0" step="any" value={row.unit_cost_pyg}
                              onChange={e => updateRow(i, "unit_cost_pyg", Number(e.target.value))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-right font-mono" data-testid={`import-materials-row-cost-${i}`} />
                            {row.price_includes_iva === true && <p className="text-[9px] text-amber-600 mt-0.5">IVA incl.</p>}
                            {row.price_includes_iva === false && <p className="text-[9px] text-gray-400 mt-0.5">sin IVA</p>}
                          </td>
                          <td className="py-1.5 px-2">
                            <select value={row.category} onChange={e => updateRow(i, "category", e.target.value)}
                              className={`w-full border rounded-lg px-1 py-1 text-xs bg-white ${row.category_needs_review ? "border-amber-400" : "border-gray-200"}`}
                              data-testid={`import-materials-row-cat-${i}`}>
                              {IMPORT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                            {row.category_needs_review && <p className="text-[9px] text-amber-600 mt-0.5">a revisar</p>}
                          </td>
                          <td className="py-1.5 px-2">
                            {row.duplicate_material_id ? (
                              <div>
                                <p className="text-[10px] text-gray-500 truncate" title={row.duplicate_material_name}>≈ {row.duplicate_material_name} ({row.duplicate_material_stock} stock)</p>
                                <select value={row.action} onChange={e => updateRow(i, "action", e.target.value)}
                                  className="w-full border border-amber-300 bg-amber-50 rounded-lg px-1 py-1 text-[10px] mt-0.5" data-testid={`import-materials-row-action-${i}`}>
                                  <option value="sum_existing">Sumar al existente</option>
                                  <option value="create_new">Crear nuevo</option>
                                </select>
                              </div>
                            ) : (
                              <span className="text-[10px] text-emerald-600 font-semibold">Nuevo material</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={handleClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">
                  Cancelar
                </button>
                <button type="button" onClick={handleConfirm} disabled={selectedCount === 0 || confirming}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="import-materials-confirm-btn">
                  {confirming ? <Loader2 size={14} className="animate-spin" /> : null}
                  Importar {selectedCount} material{selectedCount !== 1 ? "es" : ""}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  , document.body);
}
